// msk-widget.js — Mr. Serial Killer storefront chat panel
// Forked from lf-belle-widget.js. Same mechanics (session id, typing state, structured
// product cards, teaser-once-per-session); the skin is rebuilt from scratch in MSK's
// register — Void/Skull palette, IM Fell English, square plate corners, double rules.
//
// Brand laws honoured here, deliberately, because they are the difference between
// "MSK's panel" and "a chat widget with a dark theme":
//   · The void does not emit light — no gradients, no glow, no coloured shadow. Depth
//     comes from hairline rules and flat tone steps only.
//   · No emoji, anywhere, ever.
//   · Oxblood is RUBRICATION — it marks, it never fills.
//   · No urgency, no scarcity, no sales language in any string on this surface.
//
// Upload to the theme's Assets folder, then in layout/theme.liquid before </body>:
//   <script>window.__mskAvatarUrl = "{{ 'msk-avatar.png' | asset_url }}";</script>
//   <script src="{{ 'msk-widget.js' | asset_url }}" defer></script>

(function () {
  'use strict';

  const API         = 'https://belle.onlylewds.ngrok.app';
  const GREET_URL   = API + '/msk-storefront/greet';
  const CHAT_URL    = API + '/msk-storefront/chat';
  const SESSION_KEY = 'msk_chat_session_id';
  const TEASER_KEY  = 'msk_chat_teaser_seen';

  const AVATAR_URL = window.__mskAvatarUrl || '';

  // Void/Skull palette, straight off the locked brand system.
  const VOID     = '#080808';
  const ABSOLUTE = '#111111';
  const CHARCOAL = '#1C1C1C';
  const SMOKE    = '#2E2E2E';
  const ASH      = '#9A9490';
  const SKULL    = '#E8E3DA';
  const OXBLOOD  = '#8A2020';

  const DISPLAY = "'IM Fell English', 'Cormorant Garamond', Georgia, 'Times New Roman', serif";
  const BODY    = "'Cormorant Garamond', Georgia, 'Times New Roman', serif";

  // Rendered by theme.liquid inside {% if customer %} only — absent for guests, so a
  // browser cannot forge it without the server secret.
  function customer() {
    const c = window.__mskCustomer;
    return (c && c.cid && c.sig) ? { cid: c.cid, sig: c.sig } : null;
  }

  function market() {
    try {
      return {
        country:  window.Shopify?.country || 'US',
        currency: window.Shopify?.currency?.active || 'USD',
      };
    } catch { return { country: 'US', currency: 'USD' }; }
  }

  function getSessionId() {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = 'msk-' + Math.random().toString(36).slice(2) + '-' + Date.now();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  }

  let isOpen = false, isTyping = false, greeted = false, msgCount = 0;
  const sessionId = getSessionId();

  const css = `
    #msk-chat-btn {
      position: fixed; bottom: calc(24px + var(--msk-atc-h, 0px)); right: 24px;
      width: 58px; height: 58px; border-radius: 50%;
      background: ${VOID}; border: 1px solid ${SMOKE};
      cursor: pointer; z-index: 996; padding: 3px; overflow: visible;
      display: flex; align-items: center; justify-content: center;
      /* Flat, opaque shadow — a coloured or blurred glow would be light emission. */
      box-shadow: 0 2px 10px rgba(0,0,0,0.75);
      transition: border-color .2s ease, transform .2s ease, bottom .28s cubic-bezier(.4,0,.2,1);
    }
    #msk-chat-btn:hover  { border-color: ${ASH}; transform: translateY(-1px); }
    #msk-chat-btn:focus-visible { outline: 1px solid ${SKULL}; outline-offset: 3px; }
    #msk-btn-img {
      width: 100%; height: 100%; object-fit: cover; border-radius: 50%;
      display: block; box-sizing: border-box;
    }
    /* Badge reads as "this is a conversation". Bone plate, void glyph — the LF build
       proved a low-contrast badge at this size is illegible and just reads as a blip. */
    #msk-btn-badge {
      position: absolute; right: -3px; bottom: -3px;
      width: 22px; height: 22px; border-radius: 50%;
      background: ${SKULL}; border: 2px solid ${VOID};
      display: flex; align-items: center; justify-content: center;
    }
    #msk-btn-badge svg { width: 11px; height: 11px; display: block; }

    #msk-teaser {
      position: fixed; right: 92px; bottom: calc(32px + var(--msk-atc-h, 0px));
      max-width: 208px; background: ${ABSOLUTE}; color: ${SKULL};
      border: 1px solid ${SMOKE}; border-radius: 0;
      padding: 10px 30px 10px 13px;
      font-family: ${BODY}; font-size: 14px; line-height: 1.5;
      box-shadow: 0 4px 18px rgba(0,0,0,0.7); z-index: 996; cursor: pointer;
      opacity: 0; transform: translateY(6px); pointer-events: none;
      transition: opacity .3s ease, transform .3s ease;
    }
    #msk-teaser.show { opacity: 1; transform: translateY(0); pointer-events: all; }
    #msk-teaser-x {
      position: absolute; top: 2px; right: 5px; border: none; background: none;
      color: ${ASH}; font-size: 15px; line-height: 1; cursor: pointer; padding: 3px 4px;
      font-family: ${BODY};
    }
    #msk-teaser-x:hover { color: ${SKULL}; }
    @media (max-width: 749px) { #msk-teaser { display: none; } }

    #msk-unread {
      position: absolute; top: -3px; right: -3px;
      width: 9px; height: 9px; border-radius: 50%;
      background: ${OXBLOOD}; border: 1.5px solid ${VOID};
      display: none;
    }

    #msk-panel {
      position: fixed; bottom: 94px; right: 24px;
      width: 372px; height: 540px;
      background: ${VOID};
      /* Double rule — the plate convention used across every bespoke MSK section. */
      border: 1px solid ${SMOKE}; outline: 1px solid ${VOID}; outline-offset: 3px;
      border-radius: 0;
      box-shadow: 0 10px 46px rgba(0,0,0,0.8);
      z-index: 9997; display: flex; flex-direction: column;
      overflow: hidden; font-family: ${BODY};
      transform: translateY(10px); opacity: 0; pointer-events: none;
      transition: transform .26s ease, opacity .2s ease;
      /* Contains every child z-index so nothing here can ever paint over the sticky
         header or the Vessel drawer — the storewide MSK stacking rule. */
      isolation: isolate;
    }
    #msk-panel.open { transform: translateY(0); opacity: 1; pointer-events: all; }

    #msk-header {
      background: ${ABSOLUTE}; border-bottom: 1px solid ${SMOKE};
      padding: 13px 15px 12px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: space-between;
    }
    .msk-head-info { display: flex; align-items: center; gap: 11px; }
    .msk-avatar {
      width: 38px; height: 38px; border-radius: 50%;
      border: 1px solid ${SMOKE}; overflow: hidden; flex-shrink: 0;
      background: ${VOID}; display: block;
    }
    .msk-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .msk-head-name {
      color: ${SKULL}; font-family: ${DISPLAY}; font-size: 16px;
      letter-spacing: .09em; text-transform: uppercase; line-height: 1.15;
    }
    .msk-head-sub {
      color: ${ASH}; font-size: 10.5px; letter-spacing: .22em;
      text-transform: uppercase; margin-top: 3px; font-family: ${BODY};
    }
    #msk-close {
      background: none; border: none; cursor: pointer; color: ${ASH};
      padding: 4px; display: flex; align-items: center; transition: color .15s;
    }
    #msk-close:hover { color: ${SKULL}; }
    #msk-close svg { width: 17px; height: 17px; }

    #msk-messages {
      flex: 1; overflow-y: auto; padding: 16px 15px;
      display: flex; flex-direction: column; gap: 12px; scroll-behavior: smooth;
    }
    #msk-messages::-webkit-scrollbar { width: 3px; }
    #msk-messages::-webkit-scrollbar-track { background: ${VOID}; }
    #msk-messages::-webkit-scrollbar-thumb { background: ${SMOKE}; }

    .msk-msg { display: flex; animation: mskIn .22s ease; }
    .msk-msg.user { justify-content: flex-end; }
    @keyframes mskIn { from { opacity: 0; } to { opacity: 1; } }

    .msk-bubble {
      max-width: 84%; padding: 10px 13px; border-radius: 0;
      font-size: 15px; line-height: 1.62; word-break: break-word;
    }
    /* The archive speaks unboxed — a bone hairline on the left edge, like a marginal
       note in the Codex, rather than a chat bubble. The visitor gets the plate. */
    .msk-msg.msk .msk-bubble {
      background: transparent; color: ${SKULL};
      border-left: 1px solid ${SMOKE}; padding-left: 13px; max-width: 92%;
    }
    .msk-msg.user .msk-bubble {
      background: ${CHARCOAL}; color: ${SKULL}; border: 1px solid ${SMOKE};
    }
    .msk-bubble a {
      color: ${SKULL}; text-decoration: none;
      border-bottom: 1px solid ${OXBLOOD}; padding-bottom: 1px;
    }
    .msk-bubble a:hover { border-bottom-color: ${SKULL}; }
    .msk-bubble strong { font-weight: 600; letter-spacing: .02em; }
    .msk-bubble div + div { margin-top: 6px; }

    /* Product cards render from the STRUCTURED products array the API already returns —
       never parsed out of the model's prose (the OnlyLewds approach, where a reworded
       reply silently breaks the cards). Images are fetched by the browser from
       Shopify's CDN at ?width=160, so they cost zero model tokens. */
    .msk-cards { display: flex; flex-direction: column; gap: 7px; margin-top: 2px; }
    .msk-card {
      display: flex; align-items: center; gap: 11px;
      background: ${ABSOLUTE}; border: 1px solid ${SMOKE}; border-radius: 0;
      padding: 8px 11px 8px 8px; text-decoration: none !important;
      transition: border-color .16s ease, background .16s ease;
      animation: mskIn .24s ease;
    }
    .msk-card:hover { border-color: ${ASH}; background: ${CHARCOAL}; }
    .msk-card img {
      width: 48px; height: 60px; object-fit: cover; object-position: center top;
      flex-shrink: 0; background: ${CHARCOAL}; border: 1px solid ${SMOKE};
      /* Portrait crop — the catalogue is migrating to portrait thumbnails, and
         object-fit:cover survives the swap without a code change. */
    }
    .msk-card .c-info { flex: 1; min-width: 0; }
    .msk-card .c-title {
      font-family: ${DISPLAY}; font-size: 13.5px; color: ${SKULL}; line-height: 1.3;
      letter-spacing: .03em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .msk-card .c-price {
      font-size: 12.5px; color: ${ASH}; margin-top: 3px; letter-spacing: .05em;
    }
    .msk-card .c-sold { color: ${OXBLOOD}; margin-left: 7px; text-transform: uppercase;
                        font-size: 10px; letter-spacing: .12em; }
    .msk-card .c-arrow { color: ${ASH}; font-size: 15px; flex-shrink: 0; }
    .msk-card:hover .c-arrow { color: ${SKULL}; }

    #msk-typing { display: none; padding: 0 15px 10px; }
    #msk-typing.show { display: flex; }
    .msk-dots { display: flex; gap: 5px; align-items: center; padding-left: 14px; }
    .msk-dots span {
      width: 3px; height: 3px; border-radius: 50%; background: ${ASH};
      animation: mskDot 1.3s infinite;
    }
    .msk-dots span:nth-child(2) { animation-delay: .18s; }
    .msk-dots span:nth-child(3) { animation-delay: .36s; }
    @keyframes mskDot { 0%,60%,100% { opacity: .25; } 30% { opacity: 1; } }

    #msk-quick { padding: 2px 15px 10px; display: flex; gap: 6px; flex-wrap: wrap; flex-shrink: 0; }
    .msk-qr {
      background: none; border: 1px solid ${SMOKE}; color: ${ASH};
      padding: 5px 11px; border-radius: 0; font-size: 12px;
      font-family: ${BODY}; cursor: pointer; white-space: nowrap;
      letter-spacing: .07em; text-transform: uppercase;
      transition: color .15s, border-color .15s;
    }
    .msk-qr:hover { color: ${SKULL}; border-color: ${ASH}; }

    #msk-input-area {
      border-top: 1px solid ${SMOKE}; padding: 10px 12px;
      display: flex; gap: 9px; align-items: flex-end; flex-shrink: 0; background: ${ABSOLUTE};
    }
    /* Dawn's base.css styles every bare <input>/<textarea> and out-specifies a section
       level border shorthand — the LF/MSK builds hit this four separate times. Declare
       the full border explicitly and scope it tightly. */
    #msk-panel textarea#msk-input {
      flex: 1; background: ${VOID}; color: ${SKULL};
      border: 1px solid ${SMOKE}; border-radius: 0;
      padding: 9px 12px; font-size: 15px; font-family: ${BODY};
      resize: none; outline: none; max-height: 84px; min-height: 38px; line-height: 1.45;
      transition: border-color .15s; box-shadow: none;
    }
    #msk-panel textarea#msk-input:focus { border-color: ${ASH}; }
    #msk-panel textarea#msk-input::placeholder { color: ${SMOKE}; }
    #msk-send {
      width: 38px; height: 38px; border-radius: 0;
      background: ${VOID}; border: 1px solid ${SMOKE}; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; opacity: .45; transition: opacity .15s, border-color .15s;
    }
    #msk-send.ready { opacity: 1; }
    #msk-send.ready:hover { border-color: ${SKULL}; }
    #msk-send svg { width: 15px; height: 15px; }

    #msk-footer {
      text-align: center; padding: 5px 0 7px; background: ${ABSOLUTE};
      border-top: 1px solid ${VOID};
      font-size: 9px; color: ${SMOKE}; letter-spacing: .3em;
      text-transform: uppercase; flex-shrink: 0; font-family: ${BODY};
    }

    /* Corner relocation. The store runs a Reputon sticky-video app pinned bottom-right
       at z-index 999, which sat directly on top of this launcher. Winning that with a
       higher z-index would just hide the merchant's other app, so instead the launcher
       moves to the opposite corner when the intended spot is occupied (see placeLauncher).
       Self-healing: remove the app and it goes back to the right on the next load. */
    #msk-chat-root.msk-left #msk-chat-btn { right: auto; left: 24px; }
    #msk-chat-root.msk-left #msk-panel    { right: auto; left: 24px; }
    #msk-chat-root.msk-left #msk-teaser {
      right: auto; left: 92px;
      border-bottom-left-radius: 0; padding: 10px 30px 10px 13px;
    }

    @media (max-width: 480px) {
      #msk-chat-btn { bottom: 16px; right: 16px; width: 52px; height: 52px; }
      #msk-chat-root.msk-left #msk-chat-btn { left: 16px; }
      #msk-chat-root.msk-left #msk-panel { left: 0; }
      #msk-panel {
        width: 100vw; right: 0; left: 0; bottom: 0;
        height: 72dvh; min-height: 400px; max-height: 88dvh;
        border-left: none; border-right: none; border-bottom: none; outline: none;
        padding-bottom: env(safe-area-inset-bottom, 0px);
      }
      #msk-messages { -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
      #msk-input-area { padding-bottom: max(10px, env(safe-area-inset-bottom)); }
      /* 16px minimum or iOS Safari zooms the whole page on focus. */
      #msk-panel textarea#msk-input { font-size: 16px; }
      .msk-bubble { max-width: 92%; font-size: 15.5px; }
    }

    @media (prefers-reduced-motion: reduce) {
      #msk-panel, #msk-chat-btn, #msk-teaser { transition: none; }
      .msk-msg, .msk-card { animation: none; }
      .msk-dots span { animation: none; opacity: .6; }
    }
  `;

  const styleEl = document.createElement('style');
  styleEl.textContent = css;
  document.head.appendChild(styleEl);

  // The emblem is engraved dark-on-dark with its own hatched circular ground, so it
  // drops onto the void launcher as-is. If the asset is missing, fall back to the
  // wordmark initials rather than a generic chat glyph — a stock speech bubble on this
  // brand is worse than no image.
  const avatarHtml = AVATAR_URL
    ? `<img src="${AVATAR_URL}" alt="Mr. Serial Killer" />`
    : `<span style="color:${SKULL};font-family:${DISPLAY};font-size:13px;letter-spacing:.06em;
         display:flex;align-items:center;justify-content:center;width:100%;height:100%">MSK</span>`;

  const btnInner = AVATAR_URL
    ? `<img id="msk-btn-img" src="${AVATAR_URL}" alt="" />`
    : `<span style="color:${SKULL};font-family:${DISPLAY};font-size:15px;letter-spacing:.05em">MSK</span>`;

  const wrapper = document.createElement('div');
  wrapper.id = 'msk-chat-root';
  wrapper.innerHTML = `
    <div id="msk-teaser" role="button" tabindex="0">
      <button id="msk-teaser-x" aria-label="Dismiss">&times;</button>
      Ask the archive.
    </div>

    <button id="msk-chat-btn" aria-label="Ask the archive" aria-expanded="false">
      ${btnInner}
      <span id="msk-btn-badge" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="${VOID}"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/></svg>
      </span>
      <span id="msk-unread" aria-hidden="true"></span>
    </button>

    <div id="msk-panel" role="dialog" aria-label="Ask the archive">
      <div id="msk-header">
        <div class="msk-head-info">
          <div class="msk-avatar">${avatarHtml}</div>
          <div>
            <div class="msk-head-name">Mr. Serial Killer</div>
            <div class="msk-head-sub">The Archive</div>
          </div>
        </div>
        <button id="msk-close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <div id="msk-messages"></div>
      <div id="msk-typing"><div class="msk-dots"><span></span><span></span><span></span></div></div>
      <div id="msk-quick"></div>

      <div id="msk-input-area">
        <textarea id="msk-input" rows="1" placeholder="Ask" maxlength="500"></textarea>
        <button id="msk-send" aria-label="Send">
          <svg viewBox="0 0 24 24" fill="none" stroke="${SKULL}" stroke-width="1.8" stroke-linecap="round">
            <line x1="4" y1="12" x2="20" y2="12"/><polyline points="14 6 20 12 14 18"/>
          </svg>
        </button>
      </div>
      <div id="msk-footer">The record is open</div>
    </div>
  `;
  document.body.appendChild(wrapper);

  const btn      = document.getElementById('msk-chat-btn');
  const panel    = document.getElementById('msk-panel');
  const closeBtn = document.getElementById('msk-close');
  const messages = document.getElementById('msk-messages');
  const typing   = document.getElementById('msk-typing');
  const qrArea   = document.getElementById('msk-quick');
  const teaser   = document.getElementById('msk-teaser');
  const input    = document.getElementById('msk-input');
  const sendBtn  = document.getElementById('msk-send');
  const unread   = document.getElementById('msk-unread');

  // Quick replies are the fastest way to show a visitor this panel answers LORE and
  // not just orders — which is the whole reason it exists. Two of the four are record
  // questions on purpose.
  const INITIAL_QR = ['The Codex', 'The Shadows', 'What is Hexcraft', 'Shipping'];
  const QR_MAP = {
    'The Codex':        'what is the Codex',
    'The Shadows':      'who are the Shadows',
    'What is Hexcraft': 'what is Hexcraft',
    'Shipping':         'what is your shipping and return policy',
    'Go on':            'tell me more',
    'Something else':   'show me something else',
  };

  function showQuickReplies(list) {
    qrArea.innerHTML = '';
    list.forEach(label => {
      const b = document.createElement('button');
      b.className = 'msk-qr';
      b.textContent = label;
      b.onclick = () => { qrArea.innerHTML = ''; sendMessage(QR_MAP[label] || label, label); };
      qrArea.appendChild(b);
    });
  }

  function openWidget() {
    isOpen = true;
    panel.classList.add('open');
    unread.style.display = 'none';
    btn.setAttribute('aria-expanded', 'true');
    if (!greeted) { greeted = true; fetchGreeting(); }
    setTimeout(() => input.focus(), 280);
  }

  function closeWidget() {
    isOpen = false;
    panel.classList.remove('open');
    btn.setAttribute('aria-expanded', 'false');
  }

  function hideTeaser(remember) {
    if (!teaser) return;
    teaser.classList.remove('show');
    if (remember) { try { sessionStorage.setItem(TEASER_KEY, '1'); } catch (_) {} }
  }

  // Once per browser session, 7s in, and dismissing sticks. A teaser that comes back
  // is a nag, and a nag on this brand reads as the wrong kind of insistent.
  if (teaser && !sessionStorage.getItem(TEASER_KEY)) {
    setTimeout(() => { if (!isOpen) teaser.classList.add('show'); }, 7000);
    teaser.addEventListener('click', (e) => {
      if (e.target.id === 'msk-teaser-x') { e.stopPropagation(); hideTeaser(true); return; }
      hideTeaser(true); btn.click();
    });
    teaser.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); hideTeaser(true); btn.click(); }
    });
  }

  btn.addEventListener('click', (e) => {
    hideTeaser(true); e.stopPropagation();
    isOpen ? closeWidget() : openWidget();
  });
  closeBtn.addEventListener('click', (e) => { e.stopPropagation(); closeWidget(); });

  document.addEventListener('click', (e) => {
    if (!isOpen || isTyping) return;              // never close mid-reply
    if (panel.contains(e.target) || btn.contains(e.target)) return;
    closeWidget();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && isOpen) closeWidget(); });

  function esc(s) {
    return String(s).replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  // Max 3 — enough to choose from without burying the conversation.
  function appendCards(products) {
    if (!Array.isArray(products) || !products.length) return;
    const wrap = document.createElement('div');
    wrap.className = 'msk-cards';
    wrap.innerHTML = products.slice(0, 3).map(p => {
      // NOT loading="lazy". These thumbs live inside #msk-messages, an overflow
      // container that is scrolled to the bottom the moment they are appended —
      // Chrome deferred them indefinitely and they rendered as empty boxes even though
      // the URLs were fine (a detached Image() probe on the same src loaded instantly).
      // At most three ~10KB thumbs that are always in view, so lazy buys nothing.
      const img = p.image
        ? `<img src="${esc(p.image)}" alt="" decoding="async">`
        : `<img alt="" style="visibility:hidden">`;
      const sold  = p.available === false ? '<span class="c-sold">taken</span>' : '';
      const price = p.price ? `$${esc(p.price)}` : '';
      return `<a class="msk-card" href="${esc(p.url)}" target="_blank" rel="noopener">
        ${img}
        <div class="c-info">
          <div class="c-title">${esc(p.title)}</div>
          <div class="c-price">${price}${sold}</div>
        </div>
        <span class="c-arrow">&rsaquo;</span>
      </a>`;
    }).join('');
    // Hide a broken thumb rather than showing a torn-image glyph. Bound as a listener
    // instead of an inline onerror= so a strict CSP can never drop it.
    wrap.querySelectorAll('img[src]').forEach(im => {
      im.addEventListener('error', () => { im.style.visibility = 'hidden'; }, { once: true });
    });
    messages.appendChild(wrap);
    messages.scrollTop = messages.scrollHeight;
  }

  async function fetchGreeting() {
    showTyping(true);
    try {
      const r = await fetch(GREET_URL);
      const d = await r.json();
      showTyping(false);
      appendMessage('msk', d.response);
    } catch {
      showTyping(false);
      appendMessage('msk', 'You found the archive. Ask.');
    }
    showQuickReplies(INITIAL_QR);
  }

  async function sendMessage(text, displayText) {
    if (!text.trim() || isTyping) return;
    qrArea.innerHTML = '';
    appendMessage('user', displayText || text);
    input.value = '';
    input.style.height = 'auto';
    sendBtn.classList.remove('ready');
    isTyping = true;
    msgCount++;
    showTyping(true);
    try {
      const r = await fetch(CHAT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, sessionId, ...market(), customer: customer() }),
      });
      const d = await r.json();
      showTyping(false); isTyping = false;
      appendMessage('msk', d.response);
      appendCards(d.products);
      if (msgCount === 1) showQuickReplies(['Go on', 'Something else']);
    } catch {
      showTyping(false); isTyping = false;
      appendMessage('msk', 'The connection cut. Ask again.');
    }
  }

  function appendMessage(who, text) {
    const div = document.createElement('div');
    div.className = 'msk-msg ' + who;
    const bubble = document.createElement('div');
    bubble.className = 'msk-bubble';
    bubble.innerHTML = formatMessage(text || '');
    div.appendChild(bubble);
    messages.appendChild(div);
    messages.scrollTop = messages.scrollHeight;
    if (!isOpen && who === 'msk') unread.style.display = 'block';
  }

  // Escape first, THEN linkify — otherwise a URL in the reply could carry markup
  // through. Only bare URLs and markdown links are turned into anchors; nothing else
  // in a model reply is trusted as HTML.
  function formatMessage(text) {
    return String(text).split('\n').map(line => {
      if (!line.trim()) return '';
      let s = esc(line)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
          (_, label, url) => `<a href="${url}" target="_blank" rel="noopener">${label}</a>`)
        .replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g,
          (_, pre, url) => `${pre}<a href="${url}" target="_blank" rel="noopener">${url}</a>`);
      return `<div>${s}</div>`;
    }).join('');
  }

  function showTyping(show) {
    typing.classList.toggle('show', show);
    if (show) messages.scrollTop = messages.scrollHeight;
  }

  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 84) + 'px';
    sendBtn.classList.toggle('ready', input.value.trim().length > 0);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.value.trim()) sendMessage(input.value.trim());
    }
  });
  sendBtn.addEventListener('click', () => {
    if (input.value.trim()) sendMessage(input.value.trim());
  });

  setTimeout(() => { if (!isOpen && !greeted) unread.style.display = 'block'; }, 9000);

  // ── Corner arbitration ─────────────────────────────────────────────────────
  // Something else may already own the bottom-right — on this store it's a Reputon
  // sticky-video app at z-index 999, which covered the launcher completely.
  //
  // elementsFromPoint is exactly the right test here: it returns the paint order at a
  // point, so if the topmost thing over the launcher is not ours, we are genuinely
  // covered. A collider that sits BELOW us needs no action — we are still clickable.
  // Nothing is hardcoded about the other app, so this keeps working if it is swapped
  // or removed.
  function placeLauncher() {
    if (isOpen) return;                       // never move the corner out from under an open panel
    wrapper.classList.remove('msk-left');     // measure against the preferred position
    const r = btn.getBoundingClientRect();
    if (!r.width) return;
    const pts = [
      [r.left + r.width / 2, r.top + r.height / 2],
      [r.left + 6, r.top + 6],
      [r.right - 6, r.bottom - 6],
    ];
    const covered = pts.some(([x, y]) =>
      document.elementsFromPoint(x, y).some((el) => {
        if (wrapper.contains(el)) return false;             // our own nodes
        if (el === document.body || el === document.documentElement) return false;
        const s = getComputedStyle(el);
        // Only fixed/sticky furniture competes for a corner; ordinary page content
        // scrolls away and is not a collision.
        return (s.position === 'fixed' || s.position === 'sticky')
            && s.visibility !== 'hidden' && s.display !== 'none'
            && parseFloat(s.opacity || '1') > 0.05;
      }));
    if (covered) {
      wrapper.classList.add('msk-left');
      console.info('[msk-chat] bottom-right corner occupied — launcher moved left');
    }
  }

  // Run once the page has settled: third-party widgets inject late, and probing before
  // they exist reports a clear corner that is about to stop being clear.
  if (document.readyState === 'complete') setTimeout(placeLauncher, 2500);
  else window.addEventListener('load', () => setTimeout(placeLauncher, 2500));
  setTimeout(placeLauncher, 6000);            // second look for the slowest injectors

  let _rz;
  window.addEventListener('resize', () => { clearTimeout(_rz); _rz = setTimeout(placeLauncher, 300); });
})();
